import React, { useCallback, useEffect, useMemo, useState } from 'react'
import SelectBox from '../../../components/SelectBox'
import { insightSuggestions } from '../../../utils/singleVoucherInsightsDemoData'
import bettingService from '../../../services/bettingService'
import CustomSearchBtn from '../../../components/CustomSearchBtn'
import PaginationV2 from '../../../components/pagination_v2'
import { LoadingSpinner } from '../../../components'
import moment from 'moment/moment'
import CustomCalendarSVG from '../../../components/CustomCalendarSVG'

const formatCurrency = (value) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

const formatKickOff = (value) => {
  if (!value) return 'TBD'

  const date = new Date(value)
  if (!Number.isFinite(date.valueOf())) return 'TBD'

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

const getMobileFlag = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth <= 768
}

const pageSize = 10

const columnViewOptions = [
  { name: 'All', value: 'all' },
  { name: 'Goal line', value: 'goal' },
  { name: 'Body line', value: 'body' },
]

const LiveBettingInsightsPage = () => {
  const today = moment(new Date()).format('YYYY-MM-DD')
  const [dateRange, setDateRange] = useState({ from: today, to: today })
  const [searchTerm, setSearchTerm] = useState('')
  const [columnView, setColumnView] = useState(columnViewOptions[0])
  const [events, setEvents] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const isMobile = getMobileFlag()

  const fetchInsights = useCallback(async () => {
    const payload = {
      searchDate: dateRange.from,
      isBody: true,
    }

    if (!payload.searchDate) {
      setEvents([])
      return
    }

    setIsLoading(true)

    try {
      const response = await bettingService.getSingleInsightMatch(payload)
      const rawData =
        response?.data?.livedata ??
        response?.data?.bettingReports?.livedata ??
        response?.data?.bettingReports?.singleVoucherEvents ??
        []

      const formatted = rawData.map((event) => ({
        id:
          event.rapidId ??
          event.id ??
          event.postingNo ??
          `${event.homeTeam ?? 'event'}-${event.awayTeam ?? 'unknown'}-${event.eventTime ?? Date.now()}`,
        kickOff: event.eventTime ?? event.kickOff ?? event.createdDate ?? null,
        league: event.leagueName ?? event.league ?? 'Unknown league',
        leagueShort: event.leagueCode ?? event.leagueShort ?? '',
        homeTeam: event.homeTeamName ?? event.homeTeam ?? 'Home',
        awayTeam: event.awayTeamName ?? event.awayTeam ?? 'Away',
        goalTotal: event.goalAmount ?? event.goalTotal ?? 'all',
        bodyTotal: event.bodyAmount ?? event.bodyTotal ?? 'all',
        homeStake: Number(event.homeAmount ?? event.homeStake ?? 0) || 0,
        awayStake: Number(event.awayAmount ?? event.awayAmunt ?? event.awayStake ?? 0) || 0,
        overStake: Number(event.over ?? event.overStake ?? 0) || 0,
        underStake: Number(event.under ?? event.underStake ?? 0) || 0,
      }))

      setEvents(formatted)
    } catch (error) {
      console.error('Failed to fetch insight data', error)
      setEvents([])
    } finally {
      setIsLoading(false)
    }
  }, [dateRange.from])

  const rankedEvents = useMemo(() => {
    return events
      .map((event) => {
        const homeAwayDelta = event.homeStake - event.awayStake
        const overUnderDelta = event.overStake - event.underStake
        const priority = Math.max(Math.abs(homeAwayDelta), Math.abs(overUnderDelta))

        return {
          ...event,
          homeAwayDelta,
          overUnderDelta,
          priority,
        }
      })
      .sort((a, b) => b.priority - a.priority)
  }, [events])

  const filteredEvents = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()

    return rankedEvents.filter((event) => {
      if (!query) return true

      return (
        event.league.toLowerCase().includes(query) ||
        event.homeTeam.toLowerCase().includes(query) ||
        event.awayTeam.toLowerCase().includes(query)
      )
    })
  }, [rankedEvents, searchTerm])

  useEffect(() => {
    setCurrentPage(1)
  }, [filteredEvents.length, columnView.value])

  const totalPages = Math.max(1, Math.ceil(filteredEvents.length / pageSize))

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const paginatedEvents = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredEvents.slice(start, start + pageSize)
  }, [filteredEvents, currentPage])

  const showHomeAway = columnView.value !== 'goal'
  const showOverUnder = columnView.value !== 'body'
  const tableColumnCount = 4 + (showHomeAway ? 2 : 0) + (showOverUnder ? 2 : 0)

  const summary = useMemo(() => {
    if (filteredEvents.length === 0) {
      return {
        totalEvents: 0,
        totalExposure: 0,
        averageDelta: 0,
        topEvent: null,
      }
    }

    const totals = filteredEvents.reduce(
      (accumulator, event) => {
        accumulator.totalExposure += event.homeStake + event.awayStake + event.overStake + event.underStake
        accumulator.totalDelta += event.priority
        return accumulator
      },
      { totalExposure: 0, totalDelta: 0 },
    )

    return {
      totalEvents: filteredEvents.length,
      totalExposure: totals.totalExposure,
      averageDelta: totals.totalDelta / filteredEvents.length,
      topEvent: filteredEvents[0],
    }
  }, [filteredEvents])

  const handleSearch = useCallback(() => {
    fetchInsights()
  }, [fetchInsights])

  const startIndex = filteredEvents.length === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endIndex = filteredEvents.length === 0 ? 0 : Math.min(filteredEvents.length, currentPage * pageSize)

  if (isLoading && events.length === 0) {
    return <LoadingSpinner />
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-xl border border-customAccentBlue/20 bg-white p-5 shadow-lg shadow-customAccentBlue/15 dark:border-customSurface dark:bg-customSurface">
        <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-customAccentBlue dark:text-customHeading">
              Insights Single Feed
            </p>
            <h2 className="text-2xl font-bold text-customPrimaryText dark:text-customHeading">
              Single vouchers with the highest stake divergence
            </h2>
            <p className="text-sm text-customSecondaryText dark:text-customMuted">
              Monitor how single vouchers distribute across sides and goal lines. We surface the biggest
              deltas first so you can rebalance exposures quickly.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 rounded-xl bg-customAccentBlue/10 px-4 py-3 text-xs text-customAccentBlue dark:bg-custom_dark_primary2 dark:text-customHeading dark:border-custom_dark_secondary">
            <span>
              <strong className="font-semibold">{summary.totalEvents}</strong> events displayed
            </span>
            <span>
              Total exposure:{' '}
              <strong className="font-semibold text-customAccentBlue dark:text-customHeading">
                {formatCurrency(summary.totalExposure || 0)}
              </strong>
            </span>
            <span>
              Avg divergence:{' '}
              <strong className="font-semibold text-customAccentBlue dark:text-customHeading">
                {formatCurrency(Math.round(summary.averageDelta || 0))}
              </strong>
            </span>
          </div>
        </header>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex flex-col gap-2 rounded-xl border border-customAccentBlue/20 bg-customLightMilkWhite px-4 py-3 text-sm text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customMuted">
            Searchable Date
            <div className="flex gap-2">
              <div className='relative'>
              <input
                type="date"
                value={dateRange.from}
                onChange={(event) =>
                  setDateRange((prev) => ({
                    ...prev,
                    from: event.target.value,
                  }))
                }
                className="rounded-xl border border-transparent bg-white px-3 py-2 text-sm text-customPrimaryText outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/40 dark:bg-customSurface dark:text-customHeading"
              />
              <CustomCalendarSVG />
              </div>
              <CustomSearchBtn handleSearch={handleSearch} color='customAccentBlue' h/>
            </div>
          </label>
          <label className="flex flex-col gap-2 rounded-xl border border-customAccentBlue/20 bg-customLightMilkWhite px-4 py-3 text-sm text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customMuted">
            Search league or team
            <input
              type="search"
              autoComplete="off"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="e.g. Premier League"
              className="rounded-xl border border-transparent bg-white px-3 py-2 text-sm text-customPrimaryText outline-none transition focus:border-customAccentBlue focus:ring-2 focus:ring-customAccentBlue/40 dark:bg-customSurface dark:text-customHeading"
            />
          </label>
          <div className="flex flex-col gap-2 rounded-xl border border-customAccentBlue/20 bg-customLightMilkWhite px-4 py-3 text-sm text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customMuted">
            Filter Type
            <SelectBox
              selected={columnView}
              setSelected={setColumnView}
              options={columnViewOptions}
              placeholder="Select view"
              isMobile={isMobile}
            />
          </div>
        </div>
      </section>

      {summary.topEvent ? (
        <section className="rounded-xl border border-customAccentBlue/30 bg-white p-5 shadow-md shadow-customAccentBlue/15 dark:border-customSurface dark:bg-customSurface">
          <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-customAccentBlue dark:text-customHeading">
                Spotlight – Largest delta
              </p>
              <h3 className="text-lg font-semibold text-customPrimaryText dark:text-customHeading">
                {summary.topEvent.homeTeam} vs {summary.topEvent.awayTeam}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              {summary.topEvent.league ? (
                <span className="rounded-full bg-customAccent/10 px-3 py-1 text-xs font-semibold text-customAccentBlue dark:bg-customAccent/20 dark:text-customHeading">
                  {summary.topEvent.league}
                </span>
              ) : null}
              <span className="rounded-full bg-customSlate px-3 py-1 text-xs font-semibold text-customSecondaryText dark:bg-customSurfaceMuted dark:text-customMuted">
                {formatKickOff(summary.topEvent.kickOff)}
              </span>
            </div>
          </header>
          <div className="grid gap-4 md:grid-cols-3">
            <article className="rounded-xl border border-customAccentBlue/20 bg-customAccentBlue/10 px-4 py-3 text-sm text-customPrimaryText shadow-sm dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customHeading">
              <p className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
                Home vs Away delta
              </p>
              <span className="text-xl font-bold text-customAccent">
                {formatCurrency(Math.abs(summary.topEvent.homeAwayDelta))}
              </span>
              <p className="text-xs text-customSecondaryText dark:text-customMuted">
                Lean towards{' '}
                <strong className="font-semibold text-customAccent dark:text-customHeading">
                  {summary.topEvent.homeAwayDelta >= 0 ? 'Home' : 'Away'}
                </strong>
              </p>
            </article>
            <article className="rounded-xl border border-customAccentBlue/20 bg-customAccentBlue/10 px-4 py-3 text-sm text-customPrimaryText shadow-sm dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customHeading">
              <p className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
                Over vs Under delta
              </p>
              <span className="text-xl font-bold text-customAccentBlue">
                {formatCurrency(Math.abs(summary.topEvent.overUnderDelta))}
              </span>
              <p className="text-xs text-customSecondaryText dark:text-customMuted">
                Favouring{' '}
                <strong className="font-semibold text-customAccent dark:text-customHeading">
                  {summary.topEvent.overUnderDelta >= 0 ? 'Over' : 'Under'}
                </strong>
              </p>
            </article>
            <article className="rounded-xl border border-customAccentBlue/20 bg-customAccentBlue/10 px-4 py-3 text-sm text-customPrimaryText shadow-sm dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customHeading">
              <p className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
                Suggested follow-up
              </p>
              <p className="text-sm">
                Consider nudging odds or hedging exposure with correlated markets before cash-out opens.
              </p>
            </article>
          </div>
        </section>
      ) : null}

      <section className="overflow-hidden rounded-xl border border-customAccentBlue/20 bg-white shadow-lg shadow-customAccentBlue/10 dark:border-customSurface dark:bg-customSurface">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-customAccent/10 px-5 py-4 dark:border-customSurfaceMuted">
          <div>
            <h3 className="text-lg font-semibold text-customPrimaryText dark:text-customHeading">
              Single feed breakdown
            </h3>
            <p className="text-sm text-customSecondaryText dark:text-customMuted">
              Ordered by largest combined difference between sides and goal totals.
            </p>
          </div>
          <button
            type="button"
            className="rounded-xl border border-transparent bg-customAccentBlue px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-md shadow-customAccentBlue/30 transition hover:bg-customAccentBlue/90 focus:outline-none focus:ring-2 focus:ring-customAccentBlue/60 focus:ring-offset-2 dark:bg-customAccentBlue dark:hover:bg-customAccentBlue/80"
          >
            Export shortlist
          </button>
        </header>

        <div className="relative overflow-x-auto">
          <table className="min-w-full divide-y divide-customAccentBlue/10 text-sm dark:divide-customSurfaceMuted">
            <thead className="bg-background text-left text-xs font-semibold uppercase tracking-wide text-customLightMilkWhite dark:bg-custom_dark_primary2/60 dark:text-customMuted">
              <tr>
                <th className="px-2 py-3">No</th>
                <th className="px-4 py-3">Event</th>
                {showHomeAway ? (
                  <>
                    <th className="px-4 py-3">Home</th>
                    <th className="px-4 py-3">Away</th>
                  </>
                ) : null}
                {showOverUnder ? (
                  <>
                    <th className="px-4 py-3">Over</th>
                    <th className="px-4 py-3">Under</th>
                  </>
                ) : null}
                <th className="px-4 py-3">Difference</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-customAccent/10 bg-white dark:divide-customSurfaceMuted/80 dark:bg-customSurface">
              {paginatedEvents.length === 0 ? (
                <tr>
                  <td
                    colSpan={tableColumnCount}
                    className="px-4 py-8 text-center text-sm font-medium text-customSecondaryText dark:text-customMuted"
                  >
                    No events match the current filters. Try widening the date range or adjusting the search.
                  </td>
                </tr>
              ) : (
                paginatedEvents.map((event, index) => {
                  const rowNumber = startIndex + index
                  const homeLean = event.homeAwayDelta >= 0 ? 'Home' : 'Away'
                  const totalLean = event.overUnderDelta >= 0 ? 'Over' : 'Under'

                  const highlightMetric = (() => {
                    if (showHomeAway && showOverUnder) {
                      const useHome = Math.abs(event.homeAwayDelta) >= Math.abs(event.overUnderDelta)
                      return {
                        label: useHome ? `${homeLean} edge` : `${totalLean} edge`,
                        value: useHome
                          ? formatCurrency(Math.abs(event.homeAwayDelta))
                          : formatCurrency(Math.abs(event.overUnderDelta)),
                      }
                    }
                    if (showHomeAway) {
                      return {
                        label: `${homeLean} edge`,
                        value: formatCurrency(Math.abs(event.homeAwayDelta)),
                      }
                    }
                    if (showOverUnder) {
                      return {
                        label: `${totalLean} edge`,
                        value: formatCurrency(Math.abs(event.overUnderDelta)),
                      }
                    }
                    return { label: 'Edge', value: formatCurrency(0) }
                  })()

                  return (
                    <tr
                      key={event.id}
                      className="transition hover:bg-customAccentBlue/5 dark:hover:bg-customSurfaceMuted/70"
                    >
                      <td className="px-2 py-2 text-xs font-semibold text-customSecondaryText dark:text-customMuted">
                        {String(rowNumber).padStart(2, '0')}
                      </td>
                      <td className="px-4 py-2 text-sm font-semibold text-customPrimaryText dark:text-customHeading">
                        <div className="flex flex-col">
                          <span className="text-xs text-customSecondaryText dark:text-customMuted">{event.league}</span>
                          <span>
                            {event.homeTeam} vs {event.awayTeam}
                          </span>
                          <span className="text-xs text-customSecondaryText dark:text-customMuted">
                            {formatKickOff(event.kickOff)}
                          </span>
                        </div>
                      </td>
                      {showHomeAway ? (
                        <>
                          <td className="px-4 py-2 text-sm text-customAccentBlue dark:semi-bold">
                            {formatCurrency(event.homeStake)}
                          </td>
                          <td className="px-4 py-2 text-sm text-customAccentBlue dark:semi-bold">
                            {formatCurrency(event.awayStake)}
                          </td>
                        </>
                      ) : null}
                      {showOverUnder ? (
                        <>
                          <td className="px-4 py-2 text-sm text-customFontVoilet dark:semi-bold">
                            {formatCurrency(event.overStake)}
                          </td>
                          <td className="px-4 py-2 text-sm text-customFontVoilet dark:semi-bold">
                            {formatCurrency(event.underStake)}
                          </td>
                        </>
                      ) : null}
                      <td className="px-4 py-2 text-xs">
                        <div className="flex flex-col gap-1">
                          {showHomeAway ? (
                            <span className="font-semibold text-customAccentBlue">
                              {homeLean} edge – {formatCurrency(Math.abs(event.homeAwayDelta))}
                            </span>
                          ) : null}
                          {showOverUnder ? (
                            <span className="font-semibold text-customFontVoilet">
                              {totalLean} edge – {formatCurrency(Math.abs(event.overUnderDelta))}
                            </span>
                          ) : null}
                          <span className="rounded-lg bg-customVibrantGreen/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-customVibrantGreen dark:bg-customAccent/20 dark:text-customHeading">
                            {highlightMetric.label} – {highlightMetric.value}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <button
                            type="button"
                            className="w-full rounded-xl border border-transparent bg-customAccentBlue px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm shadow-customAccentBlue/30 transition hover:bg-customAccentBlue/90 focus:outline-none focus:ring-2 focus:ring-customAccentBlue/60 focus:ring-offset-2 dark:bg-customAccent dark:hover:bg-customAccent/80"
                          >
                            Watch
                          </button>
                          <button
                            type="button"
                            className="w-full rounded-xl border border-customAccentBlue/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-customAccentBlue transition hover:border-customAccentBlue/50 hover:bg-customAccentBlue/10 dark:border-customSurfaceMuted dark:text-customHeading dark:bg-custom_dark_primary2"
                          >
                            Hedge plan
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationV2
          startIndex={startIndex}
          endIndex={endIndex}
          totalItems={filteredEvents.length}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </section>

      {/* <section className="rounded-2xl border border-dashed border-customAccent/30 bg-customSlate/70 p-5 text-sm shadow-inner dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/70 dark:text-customMuted">
        <h3 className="text-base font-semibold text-customPrimaryText dark:text-customHeading">
          Suggested enhancements
        </h3>
        <ul className="mt-3 grid gap-3 md:grid-cols-3">
          {insightSuggestions.map((tip, index) => (
            <li
              key={tip}
              className="rounded-xl bg-white px-3 py-3 text-sm font-medium text-customPrimaryText shadow-sm dark:bg-customSurface dark:text-customHeading"
            >
              <span className="mr-2 text-xs font-semibold text-customAccent dark:text-customHeading">
                #{index + 1}
              </span>
              {tip}
            </li>
          ))}
          <li className="rounded-xl bg-white px-3 py-3 text-sm font-medium text-customPrimaryText shadow-sm dark:bg-customSurface dark:text-customHeading">
            Pair this table with a margin-impact chart so risk teams see projected profit swings instantly.
          </li>
        </ul>
      </section> */}
    </div>
  )
}

export default LiveBettingInsightsPage
